import React, { useState, useEffect, useCallback } from "react";
import { authAxios, API } from "@/contexts/AuthContext";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { RefreshCw, Users, Loader2 } from "lucide-react";

export default function GroupsPage() {
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncing, setSyncing] = useState(false);
  const [toggling, setToggling] = useState(null);

  const fetchGroups = useCallback(async () => {
    try { const { data } = await authAxios.get(`${API}/groups`); setGroups(data); }
    catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);

  const handleSync = async () => {
    setSyncing(true);
    try {
      const { data } = await authAxios.post(`${API}/groups/sync`);
      alert(data.message || "Synced");
      fetchGroups();
    } catch (err) {
      alert(err.response?.data?.detail || "Sync failed. Make sure WhatsApp is connected.");
    } finally { setSyncing(false); }
  };

  const handleToggle = async (group, enabled) => {
    setToggling(group.id);
    try {
      await authAxios.put(`${API}/groups/${group.id}/toggle`, { is_enabled: enabled });
      setGroups(groups.map(g => g.id === group.id ? { ...g, is_enabled: enabled } : g));
    } catch (err) { console.error(err); }
    finally { setToggling(null); }
  };

  if (loading) return <div className="p-8"><div className="animate-pulse h-64 bg-slate-200 rounded-xl" /></div>;

  return (
    <div className="p-8" data-testid="groups-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-semibold text-[#0f172a] flex items-center gap-2"><Users className="w-6 h-6" /> Group Selection</h1>
          <p className="text-sm text-muted-foreground mt-1">Choose which groups the bot is active in</p>
        </div>
        <Button onClick={handleSync} disabled={syncing} variant="outline" data-testid="sync-groups-btn">
          {syncing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-2" />}
          {syncing ? "Syncing..." : "Sync Groups from WhatsApp"}
        </Button>
      </div>

      {groups.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            <Users className="w-10 h-10 mx-auto mb-3 opacity-30" />
            <p className="text-sm">No groups found</p>
            <p className="text-xs mt-1">Connect WhatsApp first, then click "Sync Groups" to fetch your group chats</p>
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardHeader><CardTitle className="text-base">{groups.length} Groups</CardTitle></CardHeader>
          <CardContent className="space-y-1 p-0">
            {groups.map((group) => (
              <div key={group.id} className="flex items-center justify-between px-6 py-3 border-b last:border-0 hover:bg-slate-50" data-testid={`group-${group.id}`}>
                <div className="flex items-center gap-3">
                  <div className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><Users className="w-4 h-4 text-slate-500" /></div>
                  <div>
                    <p className="text-sm font-medium">{group.group_name}</p>
                    <p className="text-xs text-muted-foreground truncate max-w-xs">{group.group_chat_id}</p>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleGroupMode(group, "open")}
                    disabled={toggling === group.id}
                    title="Abrir grupo: todos pueden escribir"
                    data-testid={`open-group-${group.id}`}
                  >
                    <Unlock className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Abrir
                  </Button>
                  <Button
                    size="sm" variant="outline"
                    onClick={() => handleGroupMode(group, "close")}
                    disabled={toggling === group.id}
                    title="Cerrar grupo: solo admins"
                    data-testid={`close-group-${group.id}`}
                  >
                    <Lock className="w-3.5 h-3.5 mr-1 text-red-600" /> Cerrar
                  </Button>
                  <Badge className={group.is_enabled ? "bg-[#22c55e] text-white" : "bg-slate-200 text-slate-600"}>{group.is_enabled ? "Bot ON" : "Bot OFF"}</Badge>
                  <Switch
                    checked={group.is_enabled}
                    onCheckedChange={(v) => handleToggle(group, v)}
                    disabled={toggling === group.id}
                    className="data-[state=checked]:bg-[#22c55e]"
                    data-testid={`toggle-group-${group.id}`}
                  />
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}
    </div>
  );
}
